import { useState, useRef } from "react";
import { useAppStore } from "lib/store/useAppStore";
import { useNavigate } from "react-router-dom";
import { PostCreateProject } from "lib/api/postCreateProject";
import { useTranslation } from "react-i18next";
import HelpTip from "components/HelpTip";
import DateInput from "components/project/DateInput";
import { ProjectTreePathEditor } from "components/project/ProjectTreeEditor";
import {
  emptyProjectPath,
  normalizeProjectPaths,
} from "lib/utils/projectNodePaths";
import "css/projectWorkspace.css";
import {
  focusProjectCreateField,
  getFirstEmptyProjectCreateField,
  showProjectCreateRequiredAlert,
} from "lib/utils/projectCreateFormValidation";

export default function NewProjectPage() {
  const user = useAppStore((s) => s.user || {});
  const { id } = user;
  const hasUserOrganization = Boolean(user?.organization_id);
  const { t } = useTranslation();
  const setLastSelectedProject = useAppStore((s) => s.setLastSelectedProject);
  const fetchProjectTree = useAppStore((s) => s.fetchProjectTree);
  const showAlert = useAppStore((s) => s.showAlert);
  const navigate = useNavigate();
  const [treePaths, setTreePaths] = useState([emptyProjectPath()]);
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    project_name: "",
    contract_name: "",
    period_start: "",
    period_end: "",
    contractor: "",
    participants: "",
    purpose: "",
    project_scope: "personal",
  });

  const inputRefs = {
    project_name: useRef(null),
    contract_name: useRef(null),
    period_start: useRef(null),
    period_end: useRef(null),
    contractor: useRef(null),
    participants: useRef(null),
    purpose: useRef(null),
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    if (!id) {
      showAlert({
        textKey: "api.auth.token.missing",
        success: false,
        autoClose: false,
      });
      return;
    }

    // 빈 값 검증 (첫 번째 빈 필드에 포커스)
    const emptyField = getFirstEmptyProjectCreateField(form);

    if (emptyField) {
      showProjectCreateRequiredAlert({
        showAlert,
        inputRefs,
        emptyField,
      });
      return;
    }

    const start = new Date(form.period_start);
    const end = new Date(form.period_end);
    if (form.period_start && form.period_end && end < start) {
      showAlert({
        textKey: "api.project.create.validation.invalidPeriod",
        success: false,
        autoClose: false,
      });
      focusProjectCreateField(inputRefs, "period_end");
      return;
    }

    const payload = {
      ...form,
      project_scope: hasUserOrganization ? form.project_scope : "personal",
      tree_paths: normalizeProjectPaths(treePaths),
    };

    try {
      setIsSaving(true);
      const {
        success,
        message,
        messageKey,
        messageParams,
        project_id,
        project_name,
      } = await PostCreateProject(payload);

      if (!success) {
        showAlert({
          text: message,
          messageKey,
          messageParams,
          success: false,
          autoClose: false,
        });
        return;
      }

      setLastSelectedProject(project_id, project_name);
      await fetchProjectTree(id, project_id);

      showAlert({
        textKey: "api.project.create.success",
        success: true,
        autoClose: true,
        duration: 2000,
        afterClose: () => navigate("/project"),
      });
    } catch (err) {
      console.error(err);
      showAlert({
        textKey: "api.project.create.failed",
        success: false,
        autoClose: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="content projectSettingsPage projectCreatePage">
      <div className="projectSettings__heading">
        <div>
          <div className="helpTitleRow">
            <h2>{t("newProject.title")}</h2>
            <HelpTip tipKey="CREATE_PROJECT" />
          </div>
          <p>
            기본 설정과 보고서 기재 정보를 입력하세요. 현장 경로는 선택
            사항입니다.
          </p>
        </div>
        <div className="projectCreatePage__headingActions">
        <button
          type="button"
          className="projectSettings__secondary"
          disabled={isSaving}
          onClick={() => navigate("/project")}
        >
          프로젝트 목록으로
        </button>
        <button className="btn-bg-blue" type="submit" form="createProjectForm" disabled={isSaving}>
          {isSaving ? "프로젝트 생성 중…" : t("newProject.createSubmitButton")}
        </button>
        </div>
      </div>
      <form id="createProjectForm" onSubmit={handleSubmit} className="projectCreatePage__form">
        <section
          className="projectSettings__section"
          aria-labelledby="createProjectBasics"
        >
          <div className="projectCreatePage__sectionHeading">
            <span>1</span>
            <div>
              <h3 id="createProjectBasics">기본 설정</h3>
              <p>프로젝트 이름과 공유 대상을 정합니다.</p>
            </div>
          </div>
          <div className="projectSettings__formGrid">
            <label className="projectSettings__wide">
              <span>{t("newProject.projectNameLabel")}</span>
              <input
                ref={inputRefs.project_name}
                onChange={handleChange}
                value={form.project_name}
                id="project_name"
                type="text"
                disabled={isSaving}
                maxLength={255}
                placeholder={t("newProject.projectNamePlaceholder")}
              />
            </label>
            <label className="projectSettings__wide">
              <span>공개 범위</span>
              <select
                onChange={handleChange}
                value={form.project_scope}
                id="project_scope"
                disabled={isSaving}
              >
                <option value="personal">나만 보기</option>
                <option value="organization" disabled={!hasUserOrganization}>
                  조직 공개
                </option>
              </select>
            </label>
          </div>
        </section>
        <section
          className="projectSettings__section"
          aria-labelledby="createProjectReport"
        >
          <div className="projectCreatePage__sectionHeading">
            <span>2</span>
            <div>
              <h3 id="createProjectReport">보고서 기재 정보</h3>
              <p>
                보고서에 사용되는 내용입니다. 생성 후 보고서 메뉴에서 수정할 수
                있습니다.
              </p>
            </div>
          </div>
          <div className="projectSettings__formGrid">
            <label className="projectSettings__wide">
              <span>{t("newProject.contractNameLabel")}</span>
              <input
                ref={inputRefs.contract_name}
                onChange={handleChange}
                value={form.contract_name}
                id="contract_name"
                type="text"
                disabled={isSaving}
                maxLength={255}
                placeholder={t("newProject.contractNamePlaceholder")}
              />
            </label>
            <div className="projectCreatePage__period">
              <label htmlFor="period_start">진단 시작일</label>
              <label htmlFor="period_end">진단 종료일</label>
              <DateInput
                ref={inputRefs.period_start}
                setForm={setForm}
                value={form.period_start}
                htmlFor="period_start"
                readOnly={isSaving}
              />
              <DateInput
                ref={inputRefs.period_end}
                setForm={setForm}
                value={form.period_end}
                htmlFor="period_end"
                readOnly={isSaving}
              />
            </div>
            {[
              ["contractor", "contractorLabel", "contractorPlaceholder"],
              ["participants", "participantsLabel", "participantsPlaceholder"],
            ].map(([key, label, placeholder]) => (
              <label className="projectSettings__wide" key={key}>
                <span>{t(`newProject.${label}`)}</span>
                <input
                  ref={inputRefs[key]}
                  onChange={handleChange}
                  value={form[key]}
                  id={key}
                  type="text"
                  maxLength={key === "participants" ? 500 : 255}
                  disabled={isSaving}
                  placeholder={t(`newProject.${placeholder}`)}
                />
              </label>
            ))}
            <label className="projectCreatePage__purpose">
              <span>{t("newProject.purposeLabel")}</span>
              <textarea
                ref={inputRefs.purpose}
                onChange={handleChange}
                value={form.purpose}
                id="purpose"
                rows={3}
                disabled={isSaving}
                placeholder={t("newProject.purposePlaceholder")}
              />
            </label>
          </div>
        </section>
        <section
          className="projectSettings__section"
          aria-labelledby="createProjectTree"
        >
          <div className="projectCreatePage__sectionHeading">
            <span>3</span>
            <div>
              <h3 id="createProjectTree">
                현장 경로 <small>선택</small>
              </h3>
              <p>
                파일이 없어도 건물·층·위치를 미리 등록할 수 있습니다. 나중에
                프로젝트 설정에서도 추가할 수 있습니다.
              </p>
            </div>
          </div>
          <ProjectTreePathEditor
            paths={treePaths}
            onChange={setTreePaths}
            disabled={isSaving}
          />
        </section>
      </form>
    </section>
  );
}
